import { useLang } from '../i18n'
import { BidUsage } from './BidUsage'
import { BidMembers } from './BidMembers'
import { BidDetailsCard } from './BidDetailsCard'
import { BrandingCard } from './BrandingCard'
import { IndexLimitCard } from './IndexLimitCard'
import { VersionRetentionCard } from './VersionRetentionCard'

/**
 * A bid's settings page — where the bid-member roster, the AI token-usage widget and the bid's
 * branding used to be scattered across the overview (BidHome) and the editor toolbar's palette
 * icon. Reached from the "Bid settings" button on the overview (see App.tsx#settingsPath).
 *
 * The bid's details (Thor ID, client, name — BidDetailsCard) come first. Roster and usage
 * share the next line (same 50/50 row BidHome used); the Office templates (BrandingCard — Word,
 * PowerPoint, colours, each resolved bid → corporate) get their own, below,
 * since they need more room than a half-width column gives them. Version retention (AB#245) closes
 * the page: a bid admin's counts over the tenant's ages, with the dry-run preview.
 */
export function BidSettingsPage({ bid, bidName }: { bid: string | null; bidName: string }) {
  const { t } = useLang()

  return (
    <main className="bidhome" aria-label={`${bidName} — ${t('bid_settings_title')}`}>
      <div className="bidhome-head">
        <div>
          {/* No "Opportunity · <id>" chip here either — same dedup as the overview. */}
          <h1>{t('bid_settings_title')}</h1>
          <p>{t('bid_settings_sub')}</p>
        </div>
      </div>

      {/* First block: the bid's identity (Thor ID, client, name) — "Client - Bid name" is its label everywhere. */}
      {bid && <BidDetailsCard bid={bid} />}

      {bid && (
        <div className="bidhome-row">
          <BidMembers bid={bid} />
          <BidUsage bid={bid} variant="card" />
        </div>
      )}

      {bid && <BrandingCard bid={bid} />}
      {bid && <IndexLimitCard bid={bid} />}
      {bid && <VersionRetentionCard bid={bid} />}
    </main>
  )
}
